// Todo Remove this file in future

import * as mutations from 'src/graphql/mutations'
import * as queries from 'src/graphql/queries'
import { graphqlApi } from 'src/redux/api/graphql/graphqlApi'
import type { ReduxFreelancers, ReduxUserInfo } from 'src/types/apiType'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import { makeErrorToast, msg } from 'src/utils/makeToast'

export const freelancersApi = graphqlApi.injectEndpoints({
	endpoints: (builder) => ({
		fetchFreelancerInfo: builder.query({
			query: ({
				freelancerTenantId,
			}: {
				freelancerTenantId: string
			}) => ({
				queryName: 'freelancersById',
				data: {
					freelancerTenantId,
					limit: 1000,
				},
				isList: true,
			}),
			transformResponse: (response: ReduxFreelancers[]) => {
				return response[0]
			},
			providesTags: (result) =>
				result?.freelancerTenantId
					? [{ type: 'Freelancers', id: result.freelancerTenantId }]
					: [{ type: 'Freelancers' }],
		}),
		fetchFreelancerInfoByEmail: builder.query({
			queryFn: async ({ email }: { email: string }) => {
				const {
					data: {
						userInfoByEmail: { items: freelancerInfoArray },
					},
				} = (await API.graphql(
					graphqlOperation(queries.userInfoByEmail, {
						email: email.toLowerCase()?.trim(),
						filter: { cognitoGroup: { contains: 'freelancer' } },
					})
				)) as {
					data: { userInfoByEmail: { items: ReduxUserInfo[] } }
				}
				const freelancerData = freelancerInfoArray?.[0]
				if (!freelancerData) {
					return { data: null }
				}
				return { data: freelancerData }
			},
			providesTags: (result) =>
				result?.id ? [{ type: 'Freelancers', id: result.id }] : [],
		}),
		updateCurrentCompanyFreelancer: builder.mutation({
			queryFn: async ({
				tenantId,
				name,
				freelancerInfo,
			}: {
				tenantId: string
				name: string
				freelancerInfo: ReduxFreelancers
			}) => {
				try {
					const {
						data: { getFreelancers: latestFreelancerInfo },
					} = (await API.graphql(
						graphqlOperation(queries.getFreelancers, {
							id: freelancerInfo.id,
						})
					)) as {
						data: { getFreelancers: ReduxFreelancers }
					}
					const updateInput: Partial<ReduxFreelancers> = {
						id: latestFreelancerInfo.id,
						currentCompanyName: name,
						currentCompanyTenantId: tenantId,
					}

					const {
						data: { updateFreelancers: updatedFreelancer },
					} = (await API.graphql(
						graphqlOperation(mutations.updateFreelancers, {
							input: updateInput,
						})
					)) as {
						data: { updateFreelancers: ReduxFreelancers }
					}
					return { data: updatedFreelancer }
				} catch (error: any) {
					makeErrorToast(msg)
					return { error }
				}
			},
			async onQueryStarted(
				{ tenantId, name, freelancerInfo },
				{ dispatch, queryFulfilled }
			) {
				const patchResult = dispatch(
					freelancersApi.util.updateQueryData(
						'fetchFreelancerInfo',
						{
							freelancerTenantId:
								freelancerInfo.freelancerTenantId as string,
						},
						(draft) => {
							if (draft && draft?.id === freelancerInfo.id) {
								draft.currentCompanyName = name
								draft.currentCompanyTenantId = tenantId
							}
						}
					)
				)
				try {
					await queryFulfilled
				} catch {
					patchResult.undo()
				}
			},
			invalidatesTags: (result, error, { freelancerInfo }) => [
				{ type: 'Freelancers', id: freelancerInfo.id as string },
			],
		}),
	}),
	overrideExisting: false,
})

export const {
	useFetchFreelancerInfoQuery,
	useUpdateCurrentCompanyFreelancerMutation,
	useFetchFreelancerInfoByEmailQuery,
} = freelancersApi
